import { useState, useEffect } from 'react';
import {
  Search, Filter, MoreVertical, Shield, ShieldAlert, ShieldOff,
  User as UserIcon, Loader2, X, AlertTriangle, Check, Ban
} from 'lucide-react';
import { Button } from '@/components/common';

const API_URL = import.meta.env.VITE_API_URL || '';

const getToken = () => localStorage.getItem('auth_token');

interface User {
  id: string;
  email: string;
  name: string;
  name_ko?: string;
  avatar_url?: string;
  role: 'admin' | 'moderator' | 'user';
  status: 'active' | 'suspended' | 'banned';
  suspended_until?: string;
  points: number;
  tokens: number;
  level: number;
  is_verified: number;
  country_code?: string;
  created_at: string;
  updated_at: string;
}

interface UserDetail extends User {
  bio?: string;
  location?: string;
  language?: string;
}

interface UserActivity {
  posts_count: number;
  comments_count: number;
  reviews_count: number;
}

interface ModerationLog {
  id: string;
  action: string;
  reason?: string;
  admin_name: string;
  created_at: string;
}

function RoleBadge({ role }: { role: string }) {
  const badges: Record<string, { icon: React.ElementType; className: string; label: string }> = {
    admin: { icon: Shield, className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Admin' },
    moderator: { icon: ShieldAlert, className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Moderator' },
    user: { icon: UserIcon, className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', label: 'User' },
  };

  const badge = badges[role] || badges.user;
  const Icon = badge.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
      <Icon className="w-3 h-3" />
      {badge.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const badges: Record<string, { className: string; label: string }> = {
    active: { className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Active' },
    suspended: { className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Suspended' },
    banned: { className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Banned' },
  };

  const badge = badges[status] || badges.active;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal states
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivity | null>(null);
  const [moderationHistory, setModerationHistory] = useState<ModerationLog[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'role' | 'status' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form states
  const [newRole, setNewRole] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [suspendDays, setSuspendDays] = useState('7');
  const [actionReason, setActionReason] = useState('');

  const limit = 20;

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_URL}/api/admin/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search, roleFilter, statusFilter]);

  const fetchUserDetails = async (userId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data.user);
        setUserActivity(data.activity);
        setModerationHistory(data.moderationHistory || []);
        setShowUserModal(true);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const openActionModal = (user: User, type: 'role' | 'status') => {
    setSelectedUser(user as UserDetail);
    setActionType(type);
    setNewRole(user.role);
    setNewStatus(user.status);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleRoleChange = async () => {
    if (!selectedUser || !newRole) return;
    setActionLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole, reason: actionReason }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'User role updated successfully' });
        setShowActionModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to update role' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update role' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedUser || !newStatus) return;
    setActionLoading(true);

    try {
      let suspendedUntil: string | undefined;
      if (newStatus === 'suspended' && suspendDays) {
        const date = new Date();
        date.setDate(date.getDate() + parseInt(suspendDays));
        suspendedUntil = date.toISOString();
      }

      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          suspended_until: suspendedUntil,
          reason: actionReason,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'User status updated successfully' });
        setShowActionModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to update status' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          User Management
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Manage user accounts, roles, and permissions
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-xl flex items-center justify-between ${
          message.type === 'success'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
            <option value="user">User</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>
          <Button type="submit" className="bg-coral-500 hover:bg-coral-600">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No users found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-coral-100 dark:bg-coral-900/30 rounded-full flex items-center justify-center">
                              <span className="text-coral-600 dark:text-coral-400 font-medium">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RoleBadge role={user.role} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={user.status} />
                        {user.suspended_until && (
                          <p className="text-xs text-gray-500 mt-1">
                            Until {new Date(user.suspended_until).toLocaleDateString()}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-gray-900 dark:text-white">Lv.{user.level}</span>
                        <p className="text-xs text-gray-500">{user.points} pts</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => fetchUserDetails(user.id)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <UserIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => openActionModal(user, 'role')}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                            title="Change Role"
                          >
                            <Shield className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => openActionModal(user, 'status')}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                            title="Change Status"
                          >
                            <Ban className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t dark:border-slate-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} users
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                User Details
              </h2>
              <button
                onClick={() => setShowUserModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="flex items-start gap-4">
                {selectedUser.avatar_url ? (
                  <img
                    src={selectedUser.avatar_url}
                    alt=""
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 bg-coral-100 dark:bg-coral-900/30 rounded-full flex items-center justify-center">
                    <span className="text-2xl text-coral-600 dark:text-coral-400 font-medium">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedUser.name}
                    {selectedUser.name_ko && (
                      <span className="text-gray-500 ml-2">({selectedUser.name_ko})</span>
                    )}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <RoleBadge role={selectedUser.role} />
                    <StatusBadge status={selectedUser.status} />
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {userActivity?.posts_count || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Posts</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {userActivity?.comments_count || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Comments</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {userActivity?.reviews_count || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Reviews</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b dark:border-slate-700">
                  <span className="text-gray-500 dark:text-gray-400">Level</span>
                  <span className="text-gray-900 dark:text-white">Lv.{selectedUser.level} ({selectedUser.points} pts)</span>
                </div>
                <div className="flex justify-between py-2 border-b dark:border-slate-700">
                  <span className="text-gray-500 dark:text-gray-400">Tokens</span>
                  <span className="text-gray-900 dark:text-white">{selectedUser.tokens}</span>
                </div>
                {selectedUser.location && (
                  <div className="flex justify-between py-2 border-b dark:border-slate-700">
                    <span className="text-gray-500 dark:text-gray-400">Location</span>
                    <span className="text-gray-900 dark:text-white">{selectedUser.location}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b dark:border-slate-700">
                  <span className="text-gray-500 dark:text-gray-400">Joined</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(selectedUser.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Moderation History */}
              {moderationHistory.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                    Moderation History
                  </h4>
                  <div className="space-y-2">
                    {moderationHistory.map((log) => (
                      <div
                        key={log.id}
                        className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {log.action.replace('_', ' ')}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400">
                          By {log.admin_name}
                          {log.reason && ` - ${log.reason}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {actionType === 'role' ? 'Change User Role' : 'Change User Status'}
              </h2>
              <button
                onClick={() => setShowActionModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <div className="w-10 h-10 bg-coral-100 dark:bg-coral-900/30 rounded-full flex items-center justify-center">
                  <span className="text-coral-600 dark:text-coral-400 font-medium">
                    {selectedUser.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedUser.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                </div>
              </div>

              {actionType === 'role' ? (
                <div>
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                    New Role
                  </label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                  >
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                      New Status
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="banned">Banned</option>
                    </select>
                  </div>

                  {newStatus === 'suspended' && (
                    <div>
                      <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                        Suspend Duration (days)
                      </label>
                      <select
                        value={suspendDays}
                        onChange={(e) => setSuspendDays(e.target.value)}
                        className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                      >
                        <option value="1">1 day</option>
                        <option value="3">3 days</option>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Enter reason for this action..."
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white resize-none"
                />
              </div>

              {(newStatus === 'banned' || newStatus === 'suspended') && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl text-yellow-700 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {newStatus === 'banned'
                      ? 'This will permanently ban the user from the platform.'
                      : 'This will temporarily suspend the user\'s access.'}
                  </span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowActionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className={`flex-1 ${
                    (newStatus === 'banned' || newStatus === 'suspended')
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-coral-500 hover:bg-coral-600'
                  }`}
                  onClick={actionType === 'role' ? handleRoleChange : handleStatusChange}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
